import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { personaCreateInput, personaUpdateInput, toUserAlert } from '@xdocs/schemas';
import type { Persona, PersonaCreateInput, PersonaUpdateInput } from '@xdocs/schemas';
import { Button, Checkbox, Input, Modal, Textarea, cn, useI18n } from '@xdocs/ui';
import { applyWsAlert } from '../../lib/applyWsAlert.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { safeFieldError } from '../../i18n/safeT.js';
import { usePersonasT } from './usePersonasT.js';
import styles from './PersonaFormModal.module.css';

/**
 * `styleJsonb` is intentionally free-form JSON in the shared entity schema
 * (`personaSchema`, `packages/schemas/src/document.ts`) — no sub-field
 * (voice/art-style/narration) is pinned there. This form breaks it into the
 * three named fields F16's spec calls out ("name, voice, art-style,
 * narration, params"). There's no shared schema for that breakdown to reuse
 * (styleJsonb has no canonical shape), so `<Input>`/`<Textarea>` here use
 * RHF's built-in `required`/`maxLength` rules for immediate field feedback
 * instead of a `zodResolver` (zod isn't a direct `apps/web` dependency —
 * only `@xdocs/schemas`' ALREADY-BUILT schema instances are available here,
 * not the `zod` builder itself). The REAL boundary — the eventual WS
 * payload — is still validated through the canonical `personaCreateInput`/
 * `personaUpdateInput` schema at submit time via `.safeParse()` before
 * either op is called, satisfying E6's "one shared schema" contract for the
 * fields that schema actually owns (`name`/`description`/`isPublic`).
 */
interface PersonaFormValues {
  name: string;
  description: string;
  voice: string;
  artStyle: string;
  narrationSample: string;
  isPublic: boolean;
}

function fromStyleJsonb(styleJsonb: unknown): { voice: string; artStyle: string; narrationSample: string } {
  const obj = (styleJsonb ?? {}) as Record<string, unknown>;
  return {
    voice: typeof obj['voice'] === 'string' ? obj['voice'] : '',
    artStyle: typeof obj['artStyle'] === 'string' ? obj['artStyle'] : '',
    narrationSample: typeof obj['narrationSample'] === 'string' ? obj['narrationSample'] : '',
  };
}

function toStyleJsonb(values: PersonaFormValues): Record<string, string> {
  return { voice: values.voice, artStyle: values.artStyle, narrationSample: values.narrationSample };
}

export interface PersonaFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = create mode; a `Persona` = edit mode, pre-filled. */
  persona: Persona | null;
  /**
   * `true` when `persona` is someone ELSE's public persona being imported
   * ("Add to my personas", F16 "importing a public persona from discovery
   * into your own set") rather than the caller's own row being edited.
   * `persona`'s fields still pre-fill the form, but submit always calls
   * `onCreate` — never `onUpdate` with a foreign `personaId`, which would
   * either 403 (ownership check) or silently mutate someone else's persona.
   */
  isImport?: boolean;
  onCreate: (input: PersonaCreateInput) => Promise<unknown>;
  onUpdate: (input: PersonaUpdateInput) => Promise<unknown>;
}

/** F16 create/edit/import persona form (Modal + RHF). */
export function PersonaFormModal({ open, onOpenChange, persona, isImport = false, onCreate, onUpdate }: PersonaFormModalProps): ReactElement {
  const { locale } = useI18n();
  const { t } = usePersonasT();
  const isEdit = persona !== null && !isImport;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PersonaFormValues>({
    defaultValues: { name: '', description: '', voice: '', artStyle: '', narrationSample: '', isPublic: false },
  });

  useEffect(() => {
    if (!open) return;
    if (persona) {
      // Importing a public persona always starts as a private copy in the
      // importer's own set — never silently carries the source's `isPublic`
      // over (that's a separate, deliberate choice the importer makes).
      reset({ name: persona.name, description: persona.description ?? '', isPublic: isImport ? false : persona.isPublic, ...fromStyleJsonb(persona.styleJsonb) });
    } else {
      reset({ name: '', description: '', voice: '', artStyle: '', narrationSample: '', isPublic: false });
    }
  }, [open, persona, isImport, reset]);

  async function onSubmit(values: PersonaFormValues): Promise<void> {
    if (isEdit && persona) {
      const parsed = personaUpdateInput.safeParse({
        personaId: persona.id,
        name: values.name,
        description: values.description || null,
        styleJsonb: toStyleJsonb(values),
        isPublic: values.isPublic,
      });
      if (!parsed.success) {
        applyWsAlert(toUserAlert(parsed.error), setError, locale);
        return;
      }
      try {
        await onUpdate(parsed.data);
        onOpenChange(false);
      } catch (err) {
        applyWsAlert(err, setError, locale);
      }
      return;
    }

    const parsed = personaCreateInput.safeParse({
      name: values.name,
      ...(values.description ? { description: values.description } : {}),
      styleJsonb: toStyleJsonb(values),
      isPublic: values.isPublic,
    });
    if (!parsed.success) {
      applyWsAlert(toUserAlert(parsed.error), setError, locale);
      return;
    }
    try {
      await onCreate(parsed.data);
      onOpenChange(false);
    } catch (err) {
      applyWsAlert(err, setError, locale);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? t('personas:form.title.edit') : isImport ? t('personas:form.title.import') : t('personas:form.title.create')}
      size="md"
      scrollableBody
    >
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate className={cn(styles['form'])}>
        <Input
          label={t('personas:form.name.label')}
          required
          maxLength={160}
          placeholder={t('personas:form.name.placeholder')}
          {...optionalProp('error', safeFieldError(locale, errors.name?.message))}
          {...register('name', { required: 'errors:persona.name.required', maxLength: 160 })}
        />
        <Textarea
          label={t('personas:form.description.label')}
          maxLength={2000}
          {...optionalProp('error', safeFieldError(locale, errors.description?.message))}
          {...register('description', { maxLength: 2000 })}
        />
        <Textarea
          label={t('personas:form.voice.label')}
          maxLength={200}
          placeholder={t('personas:form.voice.placeholder')}
          {...optionalProp('error', safeFieldError(locale, errors.voice?.message))}
          {...register('voice', { maxLength: 200 })}
        />
        <Input
          label={t('personas:form.artStyle.label')}
          maxLength={200}
          {...optionalProp('error', safeFieldError(locale, errors.artStyle?.message))}
          {...register('artStyle', { maxLength: 200 })}
        />
        <Input
          label={t('personas:form.narrationSample.label')}
          maxLength={2000}
          placeholder={t('personas:form.narrationSample.placeholder')}
          {...optionalProp('error', safeFieldError(locale, errors.narrationSample?.message))}
          {...register('narrationSample', { maxLength: 2000 })}
        />
        <Checkbox label={t('personas:form.isPublic.label')} {...register('isPublic')} />
        <p className={cn(styles['hint'])}>{t('personas:form.isPublic.hint')}</p>

        <div className={cn(styles['actions'])}>
          <Button variant="primary" type="submit" state={isSubmitting ? 'loading' : 'idle'}>
            {isEdit ? t('personas:form.action.save') : isImport ? t('personas:list.action.import') : t('personas:form.action.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
